import { useCallback } from "react";
import { defaultLoginPageStrings } from "./loginPageI18n";
import type { LoginPageTranslate } from "./LoginPage";

function interpolate(text: string, vars?: Record<string, string>): string {
  if (!vars) return text;
  return Object.entries(vars).reduce((acc, [k, v]) => acc.replace(new RegExp(`\\{\\{${k}\\}\\}`, "g"), v), text);
}

export type LoginTr = (key: string, fallback: string, vars?: Record<string, string>) => string;

/**
 * Returns a stable translation function that uses the optional translate prop
 * and falls back to defaultLoginPageStrings.
 *
 * Some i18n backends (e.g. i18next with default options) return the key itself
 * for a missing key rather than null/empty. We treat a result equal to the key
 * as "missing" so the shared defaults/inline fallback still apply — otherwise a
 * host that lacks one of these keys would render the raw key string.
 */
export function useLoginTranslations(translate?: LoginPageTranslate): LoginTr {
  return useCallback(
    (key: string, fallback: string, vars?: Record<string, string>): string => {
      const fromTranslate = translate?.(key, vars);
      if (fromTranslate != null && fromTranslate !== "" && fromTranslate !== key) {
        return interpolate(fromTranslate, vars);
      }
      const fromDefaults = defaultLoginPageStrings[key];
      if (fromDefaults != null && fromDefaults !== "") return interpolate(fromDefaults, vars);
      return interpolate(fallback, vars);
    },
    [translate],
  );
}
